import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Robot, User, HeroImage, HeroCardText } from '../types';

export const adminService = {
  // ── Featured Robots ───────────────────────────────────────

  async getAllRobots(): Promise<Robot[]> {
    if (!db) return [];
    try {
      const q = query(collection(db, 'robots'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date(),
      } as Robot));
    } catch {
      return [];
    }
  },

  async toggleFeatured(robotId: string, featured: boolean): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    await updateDoc(doc(db, 'robots', robotId), {
      isFeatured: featured,
      updatedAt: Timestamp.now(),
    });
  },

  // ── Homepage Images ───────────────────────────────────────

  async getHomepageContent(): Promise<{ heroImages: HeroImage[]; heroCards: HeroCardText[] }> {
    if (!db) return { heroImages: [], heroCards: [] };
    try {
      const snap = await getDoc(doc(db, 'siteContent', 'homepage'));
      if (!snap.exists()) return { heroImages: [], heroCards: [] };
      const data = snap.data();
      return { heroImages: data.heroImages || [], heroCards: data.heroCards || [] };
    } catch {
      return { heroImages: [], heroCards: [] };
    }
  },

  async updateHomepageContent(images: HeroImage[], cards: HeroCardText[]): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    await setDoc(doc(db, 'siteContent', 'homepage'), {
      heroImages: images,
      heroCards: cards,
      updatedAt: Timestamp.now(),
    });
  },

  async updateHeroImages(images: HeroImage[]): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    // Preserve existing heroCards when only updating images
    const snap = await getDoc(doc(db, 'siteContent', 'homepage'));
    const existingCards = snap.exists() ? snap.data().heroCards || [] : [];
    await setDoc(doc(db, 'siteContent', 'homepage'), {
      heroImages: images,
      heroCards: existingCards,
      updatedAt: Timestamp.now(),
    });
  },

  async uploadHeroImage(file: File): Promise<string> {
    if (!storage) throw new Error('Firebase Storage not initialized');
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(storage, `site/hero/${Date.now()}_${safeName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  },

  async deleteHeroImage(url: string): Promise<void> {
    if (!storage) return;
    try {
      const imageRef = ref(storage, url);
      await deleteObject(imageRef);
    } catch {
      // Image may have already been deleted from storage
    }
  },

  // ── Users ─────────────────────────────────────────────────

  async getAllUsers(): Promise<User[]> {
    if (!db) return [];
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date(),
      } as User));
    } catch {
      // Fallback without orderBy if index doesn't exist
      try {
        const snapshot = await getDocs(collection(db!, 'users'));
        return snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate() || new Date(),
          updatedAt: d.data().updatedAt?.toDate() || new Date(),
        } as User));
      } catch {
        return [];
      }
    }
  },

  async setUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');
    await updateDoc(doc(db, 'users', userId), {
      role,
      updatedAt: Timestamp.now(),
    });
  },

  // ── Orphaned Robot Cleanup ──────────────────────────────────

  async removeOrphanedRobots(): Promise<{ removed: number; names: string[] }> {
    if (!db) throw new Error('Firebase not initialized');

    // 1. Get all user IDs
    const usersSnap = await getDocs(collection(db, 'users'));
    const validUserIds = new Set(usersSnap.docs.map(d => d.id));

    // 2. Get all robots
    const robotsSnap = await getDocs(collection(db, 'robots'));
    const orphaned: { id: string; name: string }[] = [];

    robotsSnap.forEach(d => {
      const data = d.data();
      if (!data.ownerId || !validUserIds.has(data.ownerId)) {
        orphaned.push({ id: d.id, name: data.name || 'Unnamed' });
      }
    });

    // 3. Delete orphaned robots
    for (const robot of orphaned) {
      await deleteDoc(doc(db, 'robots', robot.id));
    }

    return { removed: orphaned.length, names: orphaned.map(r => r.name) };
  },

  // ── Analytics ───────────────────────────────────────────────

  async trackPageView(page: string): Promise<void> {
    if (!db) return;
    try {
      await addDoc(collection(db, 'analytics'), {
        type: 'page_view',
        page,
        timestamp: Timestamp.now(),
        date: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
      });
    } catch {
      // Non-critical — don't block the user
    }
  },

  async getAnalytics(days: number = 30): Promise<{
    totalViews: number;
    viewsByDay: { date: string; count: number }[];
    viewsByPage: { page: string; count: number }[];
  }> {
    if (!db) return { totalViews: 0, viewsByDay: [], viewsByPage: [] };
    try {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      const cutoffStr = cutoff.toISOString().slice(0, 10);

      const q = query(
        collection(db, 'analytics'),
        where('type', '==', 'page_view'),
        where('date', '>=', cutoffStr),
        orderBy('date', 'asc'),
      );
      const snap = await getDocs(q);

      const byDay: Record<string, number> = {};
      const byPage: Record<string, number> = {};
      let totalViews = 0;

      snap.forEach(d => {
        const data = d.data();
        const date = data.date as string;
        const page = data.page as string;
        byDay[date] = (byDay[date] || 0) + 1;
        byPage[page] = (byPage[page] || 0) + 1;
        totalViews++;
      });

      return {
        totalViews,
        viewsByDay: Object.entries(byDay).map(([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date)),
        viewsByPage: Object.entries(byPage).map(([page, count]) => ({ page, count })).sort((a, b) => b.count - a.count),
      };
    } catch {
      return { totalViews: 0, viewsByDay: [], viewsByPage: [] };
    }
  },
};
